import React, { useCallback, useContext, useMemo } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  PapiTopic,
  PapiRequestDaSharedTopicSuppress,
  PapiRequestDaSharedTopicUnwatch,
  PapiRequestDaSharedTopicWatch,
} from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiData, ClickMoreLessTopicClickBiEvent } from '@wix/da-bi/pkg/events';
import IconButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenuV2';
import MoreIcon from '@wix/da-ds/pkg/Icons/24x24/More';
import RadioButton from '@wix/da-ds/pkg/RadioButton';

import s from './TopicActions.scss';

export interface Props {
  className?: string;
  topic: PapiTopic;
  watchTopic: (
    params: PapiRequestDaSharedTopicWatch,
    onSuccess?: () => void
  ) => void;
  unwatchTopic: (
    params: PapiRequestDaSharedTopicUnwatch,
    onSuccess?: () => void
  ) => void;
  suppressTopic: (
    params: PapiRequestDaSharedTopicSuppress,
    onSuccess?: () => void
  ) => void;
  addTopicNotification: (
    id: number,
    message: string,
    onUndo: () => void
  ) => void;
  removeNotification: (id: number) => void;
}

const TopicActions: React.FC<Props> = ({
  className,
  topic,
  watchTopic,
  unwatchTopic,
  suppressTopic,
  addTopicNotification,
  removeNotification,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { userTopicSubscription } = topic;

  const apiParams = useMemo(
    () => ({
      necoid: topic.itemId,
      source: 'topic_page',
    }),
    [topic.itemId]
  );

  const onUndo = useCallback(() => {
    switch (userTopicSubscription) {
      case 'watching':
        watchTopic(apiParams);
        break;
      case 'not-watching':
        unwatchTopic(apiParams);
        break;
      case 'suppressed':
        suppressTopic(apiParams);
        break;
      default:
        break;
    }
    removeNotification(topic.itemId);
  }, [
    userTopicSubscription,
    watchTopic,
    unwatchTopic,
    suppressTopic,
    removeNotification,
    topic,
    apiParams,
  ]);

  const isNotWatching = userTopicSubscription === 'not-watching';
  const isSuppressed = userTopicSubscription === 'suppressed';

  const handleUnwatchClick = useCallback(
    () =>
      unwatchTopic(apiParams, () =>
        addTopicNotification(
          topic.itemId,
          t('topicPage.notifications.notWatching'),
          onUndo
        )
      ),
    [t, unwatchTopic, apiParams, addTopicNotification, topic, onUndo]
  );

  const handleSuppressClick = useCallback(
    () =>
      suppressTopic(apiParams, () =>
        addTopicNotification(
          topic.itemId,
          t('topicPage.notifications.suppressed', {
            topicName: topic.name,
          }),
          onUndo
        )
      ),
    [t, suppressTopic, apiParams, addTopicNotification, topic, onUndo]
  );

  return (
    <div className={cn(s['root'], className)}>
      {!isMobile && userTopicSubscription === 'suppressed' && (
        <div className={s['suppressed-state']}>
          {t('topicPage.topicActions.suppressed', { topicName: topic.name })}
        </div>
      )}

      <IconButtonWithContextMenu
        icon={MoreIcon}
        size="medium"
        items={[
          {
            label: (
              <RadioButton
                name={'showNeutral'}
                value={'neutral'}
                checked={isNotWatching}
                readOnly
              >
                <span className={s['radio-label']}>
                  {t('topicPage.topicActions.showNeutral')}
                </span>
              </RadioButton>
            ),
            onClick: isNotWatching ? undefined : handleUnwatchClick,
            biData: BiData<ClickMoreLessTopicClickBiEvent>({
              evid: 920,
              itemid: topic.itemId,
              menu_item: 'likes',
            }),
          },
          {
            label: (
              <RadioButton
                name={'showLess'}
                value={'less'}
                checked={isSuppressed}
                readOnly
              >
                <span className={s['radio-label']}>
                  {t('topicPage.topicActions.showLess')}
                </span>
              </RadioButton>
            ),
            onClick: isSuppressed ? undefined : handleSuppressClick,
            biData: BiData<ClickMoreLessTopicClickBiEvent>({
              evid: 920,
              itemid: topic.itemId,
              menu_item: 'llt',
            }),
          },
        ]}
      />
    </div>
  );
};
TopicActions.displayName = 'TopicActions';

export default TopicActions;
